"use strict";

/* =================================================================== *
 * MAPPER REGISTRY
 *
 * Every mapper lives in its own file and is loaded on demand, the first
 * time a ROM declares it in the iNES header. Nothing is required until it
 * is needed, so a page that only ever runs NROM does not pay for the MMC5
 * or the Jaleco boards.
 *
 * Each module exports `register(Mappers)`, which installs its constructor
 * (and every number it answers to) into the shared registry below. They all
 * derive from `Mappers[0]`, so NROM is registered before anything else:
 * mapper1.js does `Mappers[1].prototype = new Mappers[0]()`, which would
 * throw otherwise.
 *
 *     0  NROM                        10  MMC4
 *     1  MMC1                        11  Color Dreams
 *     2  UxROM / UNROM               12  MMC6 (SL-5020B) -> mapper12.js
 *     3  CNROM                       13  CPROM           -> mapper13.js
 *     4  MMC3                        14  SL-1632         -> mapper14.js
 *     5  MMC5                        15  Multicart
 *     6  FFE F4xxx                   16  Bandai FCG / LZ93D50 -> mapper16.js
 *                                    17  Bandai FCG + WRAM       -> mapper17.js
                                    18  UNROM variant            -> mapper18.js
                                    27  VRC4 variant            -> mapper27.js
                                    28  Action 53               -> mapper28.js
                                    29  RET-CUFROM               -> mapper29.js
                                    30  UNROM 512               -> mapper30.js
                                    31  NSF (infiniteneslives)     -> mapper31.js
                                    32  Irem G-101              -> mapper32.js
 *    20  FDS (RAM adaptor)   -> mapper20.js
 *   153  Bandai FCG + 8 KB WRAM -> mapper153.js
 *   159  Bandai FCG + X24C01 -> mapper159.js
 *                                    19  Namcot 106
 *     21  VRC4a/VRC4c   -> vrc2.js
 *     22  VRC2a         -> vrc2.js
 *     24  VRC6a          -> vrc6.js
 *     25  VRC4b          -> vrc4.js
 *     26  VRC6b          -> vrc6.js
 *    240  Jing Ke Xin Zhuan       -> mapper240.js
 *    241  BxROM (Hengge Technology)  -> mapper241.js
 *     7  AxROM                       34  BNROM          -> mapper7.js
 *     8  MROM                        38  PCI556         -> mapper38.js
 *     9  MMC2                        66  GxROM          -> mapper66.js
 *                                    67  Sunsoft-3      -> mapper67.js
 *    37  PAL-ZZ (MMC3)              -> mapper37.js
 *    47  NES-QJ (MMC3)              -> mapper47.js
 *                                    69  Sunsoft FME-7  -> mapper69.js
 *                                    71  Codemasters
 *                                    87  J87            -> mapper87.js
 *   79  NINA-03/06                  -> mapper79.js
 *    62  Super 700-in-1 (pirate)   -> mapper62.js
 *                                    85  VRC7
 *    90  J.Y. Company ASIC          -> jycompany.js
 *    94  UN1ROM
 *   118  TxSROM
 *   119  TQROM                      -> mapper119.js
 *   140  Jaleco JF-11/JF-14         -> mapper140.js
 *   141  Sachen 8259A               -> sachen8259.js
 *    77  Irem LROG017               -> mapper77.js
 *   180  UNROM variant
 *   205  JC-016-2 (MMC3)           -> mapper205.js
 *   227  Hexin 1200-in-1            -> mapper227.js
 *   228  Action 52                   -> action52.js
 *
 * Two numbers can share a file (24/26); ALIASES below is a
 * different case, where a number is simply another name for a mapper that has
 * its own file:
 *
 *    193 -> 11  (Color Dreams)
 *
 * References:
 * - Mapper list: https://www.nesdev.org/wiki/Mapper
 * - NROM:       https://www.nesdev.org/wiki/Programming_NROM
 * - MMC1:       https://www.nesdev.org/wiki/Programming_MMC1
 * - MMC3:       https://www.nesdev.org/wiki/Programming_MMC3
 * =================================================================== */

var Mappers = {};

// Numbers already registered, so a shared board is only set up once even when
// two numbers point at the same file.
var loaded = {};

// Each entry is a function rather than a module path on purpose.
//
// A bundler can only follow `require()` when the argument is a literal.
// `require(FILES[n])` builds a context module over the whole directory: webpack
// warns ("Critical dependency: the request of a dependency is an expression"),
// bundles every mapper anyway, and can hand back a second, separate instance of
// a module. Wrapping each literal in a function keeps resolution static, so the
// split pays for itself and the prototypes stay unique.
var LOADERS = {
  0: function () {
    return require("./mapper0");
  },
  1: function () {
    return require("./mapper1");
  },
  2: function () {
    return require("./mapper2");
  },
  3: function () {
    return require("./mapper3");
  },
  4: function () {
    return require("./mapper4");
  },
  5: function () {
    return require("./mapper5");
  },
  6: function () {
    return require("./mapper6");
  },
  7: function () {
    return require("./mapper7");
  },
  8: function () {
    return require("./mapper8");
  },
  9: function () {
    return require("./mapper9");
  },
  10: function () {
    return require("./mapper10");
  },
  11: function () {
    return require("./mapper11");
  },
  12: function () {
    return require("./mapper12");
  },
  13: function () {
    return require("./mapper13");
  },
  14: function () {
    return require("./mapper14");
  },
  15: function () {
    return require("./mapper15");
  },
  16: function () {
    return require("./mapper16");
  },
  17: function () {
    return require("./mapper17");
  },
  18: function () {
    return require("./mapper18");
  },
  27: function () {
    return require("./mapper27");
  },
  28: function () {
    return require("./mapper28");
  },
  29: function () {
    return require("./mapper29");
  },
  30: function () {
    return require("./mapper30");
  },
  31: function () {
    return require("./mapper31");
  },
  32: function () {
    return require("./mapper32");
  },
  33: function () {
    return require("./mapper33");
  },
  77: function () {
    return require("./mapper77");
  },
  153: function () {
    return require("./mapper153");
  },
  159: function () {
    return require("./mapper159");
  },
  19: function () {
    return require("./mapper19");
  },
  20: function () {
    return require("./mapper20");
  },
  21: function () {
    return require("./vrc2");
  },
  22: function () {
    return require("./vrc2");
  },
  23: function () {
    return require("./vrc2");
  },
  65: function () {
    return require("./mapper65");
  },
  91: function () {
    return require("./mapper91");
  },
  24: function () {
    return require("./vrc6");
  },
  25: function () {
    return require("./vrc4");
  },
  26: function () {
    return require("./vrc6");
  },
  240: function () {
    return require("./mapper240");
  },
  241: function () {
    return require("./mapper241");
  },
  34: function () {
    return require("./mapper34");
  },
  35: function () {
    return require("./mapper35");
  },
  36: function () {
    return require("./mapper36");
  },
  37: function () {
    return require("./mapper37");
  },
  38: function () {
    return require("./mapper38");
  },
  47: function () {
    return require("./mapper47");
  },
  66: function () {
    return require("./mapper66");
  },
  67: function () {
    return require("./mapper67");
  },
  62: function () {
    return require("./mapper62");
  },
  69: function () {
    return require("./mapper69");
  },
  71: function () {
    return require("./mapper71");
  },
  79: function () {
    return require("./mapper79");
  },
  85: function () {
    return require("./mapper85");
  },
  87: function () {
    return require("./mapper87");
  },
  90: function () {
    return require("./jycompany");
  },
  94: function () {
    return require("./mapper94");
  },
  118: function () {
    return require("./mapper118");
  },
  119: function () {
    return require("./mapper119");
  },
  140: function () {
    return require("./mapper140");
  },
  141: function () {
    return require("./sachen8259");
  },
  180: function () {
    return require("./mapper180");
  },
  227: function () {
    return require("./mapper227");
  },
  205: function () {
    return require("./mapper205");
  },
  228: function () {
    return require("./action52");
  },
};

// Numbers that are just another name for a mapper that has its own file.
var ALIASES = {
  193: 11, // Color Dreams
};

// A board whose file derives from another board's constructor registers the
// base first, so `Mappers[4]` already exists when mapper118's module runs
// `Mappers[118].prototype = new Mappers[4]()`. Lazy loading gives no ordering
// on its own: a ROM declaring only mapper 118 would otherwise reach a registry
// where MMC3 has never been required. Next to LOADERS, so a bundler still sees
// both requires as literals.
var DEPENDS_ON = {
  153: 16, // Famicom Jump II is an LZ93D50 with WRAM instead of an EEPROM
  159: 16, // same LZ93D50, with a 128-byte X24C01 instead of a 24C02
  37: 4, // PAL-ZZ is an MMC3 with an outer bank latch
  47: 4, // NES-QJ is the same board with a one-bit latch
  118: 4, // TxSROM is an MMC3 with a rewired CIRAM A10 line
  119: 4, // TQROM is an MMC3 with CHR ROM/RAM sharing
  205: 4, // JC-016-2 is an MMC3 with a two-bit block select
  35: 90, // Mapper 35 is mapper 90 with 8KB WRAM
};

/**
 * Returns the constructor for mapper `n`, requiring its module first if this is
 * the first time it has been asked for. Idempotent.
 *
 * An unknown number falls back to NROM rather than throwing, so a game built
 * for an exotic board still gets to run rather than refusing to boot. Callers
 * that need to tell "runs, but wrong bank layout" apart from "runs correctly"
 * ask `_known()` first.
 */
function loadMapper(n) {
  if (typeof Mappers[n] !== "undefined") return Mappers[n];

  if (typeof ALIASES[n] !== "undefined") {
    loadMapper(ALIASES[n]);
    Mappers[n] = Mappers[ALIASES[n]];
    return Mappers[n];
  }

  if (typeof LOADERS[n] === "undefined") {
    console.warn(
      "jsnes: Mapper " + n + " not implemented, falling back to NROM.",
    );
    Mappers[n] = Mappers[0];
    return Mappers[n];
  }

  if (!loaded[n]) {
    if (typeof DEPENDS_ON[n] !== "undefined") {
      loadMapper(DEPENDS_ON[n]);
    }
    LOADERS[n]().register(Mappers);
    loaded[n] = true;
  }
  return Mappers[n];
}

/** Requires every mapper module. Used by the test suite and for pre-warming. */
function loadAll() {
  Object.keys(LOADERS).forEach(function (n) {
    loadMapper(parseInt(n, 10));
  });
  Object.keys(ALIASES).forEach(function (n) {
    loadMapper(parseInt(n, 10));
  });
}

// Exposed under leading-underscore names so they cannot collide with a mapper
// number. rom.js reaches the registry through these.
Mappers._load = loadMapper;
Mappers._loadAll = loadAll;

/**
 * True when `n` is a mapper this build can actually run.
 *
 * This exists because loadMapper() deliberately falls back to NROM for an
 * unknown number: a game with an exotic board still gets to run rather than
 * refusing to boot. Callers that need to tell "runs, but wrong bank layout"
 * apart from "runs correctly" -- nes.js reporting an unsupported ROM, the ROM
 * scanner grouping results -- check this instead of looking the number up on
 * Mappers, which by then holds the NROM fallback either way.
 */
Mappers._known = function (n) {
  return typeof LOADERS[n] !== "undefined" || typeof ALIASES[n] !== "undefined";
};

// NROM is the base class for every other mapper, so it is registered up front
// rather than on demand.
LOADERS[0]().register(Mappers);
loaded[0] = true;

if (typeof window !== "undefined") {
  window.jsnes = window.jsnes || {};
  window.jsnes.Mappers = Mappers;
}

module.exports = Mappers;
